import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { AlertTriangle } from 'lucide-react';
import { checkApiHealth } from '../../utils/api-helpers';
const POLL_MS = 5 * 60 * 1000;
/**
 * Slim global banner when `/health` fails (same signal as ApiStatus).
 * Does not block the UI; informs users that some features may not load.
 */
export const DegradedServiceBanner = () => {
    const [degraded, setDegraded] = useState(false);
    const runCheck = useCallback(async () => {
        try {
            const result = await checkApiHealth();
            setDegraded(!result.isHealthy);
        }
        catch {
            setDegraded(true);
        }
    }, []);
    useEffect(() => {
        runCheck();
        const interval = setInterval(runCheck, POLL_MS);
        const onFocus = () => {
            void runCheck();
        };
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [runCheck]);
    /*
     * This banner is `sticky top-0`, and so is the marketing header in `Layout`.
     * Two sticky elements both pinned to 0 land on top of each other, so publish
     * this one's height as `--app-banner-height` and let anything else stacking
     * below offset by it. Measured rather than hard-coded because the copy wraps
     * to a different number of lines at narrow widths.
     */
    const bannerRef = useRef(null);
    useEffect(() => {
        const root = document.documentElement;
        const el = bannerRef.current;
        if (!degraded || !el) {
            root.style.removeProperty('--app-banner-height');
            return;
        }
        const publish = () => {
            root.style.setProperty('--app-banner-height', `${Math.round(el.getBoundingClientRect().height)}px`);
        };
        publish();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
        observer?.observe(el);
        return () => {
            observer?.disconnect();
            root.style.removeProperty('--app-banner-height');
        };
    }, [degraded]);
    if (!degraded) {
        return null;
    }
    return (_jsx("div", { ref: bannerRef, 
        /*
         * Near-opaque, not a 15% tint. This banner is `sticky top-0`, so page
         * content scrolls underneath it; at `bg-amber-500/15` the surface took
         * its colour from whatever was behind, and `text-amber-950` ended up
         * dark-on-dark once the landing hero's artwork started passing through.
         * The degraded state is exactly when the message has to be legible, so
         * the background is now owned by the theme rather than by the page.
         * `backdrop-blur` covers the remaining few percent.
         */
        className: "sticky top-0 z-[100] border-b border-amber-500/40 bg-amber-100/95 dark:bg-amber-950/95 backdrop-blur-md px-3 py-2 text-sm text-amber-950 dark:text-amber-100", role: "status", "aria-live": "polite", children: _jsxs(Alert, { className: "border-0 bg-transparent p-0 shadow-none", children: [_jsx(AlertTriangle, { className: "h-4 w-4 text-amber-600 dark:text-amber-400", "aria-hidden": true }), _jsx(AlertTitle, { className: "mb-0 text-sm font-semibold", children: "Connection issue" }), _jsx(AlertDescription, { className: "text-sm opacity-90", children: "We're having trouble connecting to our servers. Some features may not load. Please try again in a moment." })] }) }));
};
